import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { SessionUser } from './session-types'

/**
 * Active "Working on" performance context. Either a tour or a standalone show.
 * Persisted client-side; no JWT mint required because it doesn't change
 * permissions — it scopes the sidebar switcher and will drive page data
 * fetching once performance scoping lands.
 */
export interface ActivePerformance {
  kind: 'tour' | 'show'
  id: string
  name: string
}

interface AuthState {
  accessToken: string | null
  refreshToken: string | null
  user: SessionUser | null
  /** Currently-selected performance in the sidebar switcher. */
  activePerformance: ActivePerformance | null
  /** Nav section expansion state — keyed by section id, persisted across reloads. */
  navExpanded: Record<string, boolean>
  setSession: (params: {
    accessToken: string
    refreshToken: string
    user: SessionUser
  }) => void
  setAccessToken: (token: string) => void
  setUser: (user: SessionUser) => void
  setActivePerformance: (p: ActivePerformance | null) => void
  setNavExpanded: (sectionId: string, expanded: boolean) => void
  logout: () => void
}

/**
 * Persisted JWT auth store. Access token is short-lived (15m by API default);
 * refresh token is long-lived (30d). The api-client refreshes on 401.
 */
export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      accessToken: null,
      refreshToken: null,
      user: null,
      activePerformance: null,
      navExpanded: {},
      setSession: ({ accessToken, refreshToken, user }) =>
        // Reset perf context on session change — the new user may not have
        // access to the previously-selected performance.
        set({ accessToken, refreshToken, user, activePerformance: null }),
      setAccessToken: (accessToken) => set({ accessToken }),
      setUser: (user) => set({ user }),
      setActivePerformance: (activePerformance) => set({ activePerformance }),
      setNavExpanded: (sectionId, expanded) =>
        set((s) => ({
          navExpanded: { ...s.navExpanded, [sectionId]: expanded },
        })),
      logout: () =>
        set({
          accessToken: null,
          refreshToken: null,
          user: null,
          activePerformance: null,
          navExpanded: {},
        }),
    }),
    { name: 'ovation.auth' },
  ),
)
